/* ──────────────────────────────────────────────────────────────────────────────
   NAMA SEVK BELGESİ — YENİ  ·  /nama-documents/new  (menü 9551, Add hakkı)

   ══ NİYE AYRI EKRAN ══════════════════════════════════════════════════════════
   /nama-documents/:id "teslimatı kaydet" ekranıdır (kalem miktarları teslim edilir,
   eksik gerekçesi istenir). Burada yapılan iş BAŞKA: belgeyi masabaşından AÇMAK.
   İkisi tek dosyada olsaydı her alan "yeni mi, teslim mi" diye çatallanırdı.

   ══ SATICI SEÇİLMEZ ═══════════════════════════════════════════════════════════
   Satıcı HER ZAMAN Sütaş'tır ve sunucu belirler (temsilcinin carisinden). Formda
   satıcı alanı YOKTUR; ekranın tepesindeki bant bunu söyler. Seçtirseydik masabaşı
   kullanıcı belgeyi başka bir satıcının adına açabilirdi.

   ══ KAYNAK: yalnız SAP veya TELSEL ════════════════════════════════════════════
   "Mobil" seçeneği yoktur — o, belgenin temsilcinin cihazında doğduğunun işaretidir
   (backend Create MOBILE'ı 400 ile reddeder).

   ══ BELGE NO ══════════════════════════════════════════════════════════════════
   (Kaynak, Belge No) çiftidir ve BENZERSİZDİR: aynı çiftle tekrar kaydedilirse
   sunucu mevcut belgeyi GÜNCELLER (upsert) — kapanmış belge ise dokunmaz.

   Belge her zaman "Teslim Bekliyor" doğar; durum seçtirilmez (kalemlerden türetilir).

   Backend: DataScope.Api/Controllers/NamaDocumentsController.cs → [HttpPost] Create
   ────────────────────────────────────────────────────────────────────────────── */
import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { s, authHeaders, Toast, getODataRows } from './listPageStyles';
import { SearchableSelect } from './ui';
import {
  DetailShell, Section, Grid, FieldText, FieldDate, FieldSelect, FieldTextarea, FieldCustom,
} from './detail/DetailShell';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { fmtMoney } from './returnShared';
import {
  lookupLabel, sanitizeDecimal, toDecimal, fetchProductUnits, convertUnitPrice,
  resolveLineVat, productVatOf,
} from './invoiceShared';
import {
  NAMA_API, NAMA_SOURCE, SOURCE_CREATE_OPTIONS, DOC_TYPE_SELECT_OPTIONS,
  DEFAULT_SELLER_NAME, SELLER_STATEMENT, SELLER_SERVER_DETERMINED, toNum,
} from './namaShared';

const LOOKUP_PAGE = 50;
const muted = 'var(--text-secondary, #6b7280)';
const border = '1px solid var(--border-color, rgba(15,23,42,0.10))';

const st = {
  sellerBand: {
    display: 'flex', gap: 12, alignItems: 'center', padding: '12px 14px', marginBottom: 12,
    background: 'linear-gradient(90deg, rgba(37,99,235,0.10), rgba(37,99,235,0.02))',
    border: '1px solid rgba(37,99,235,0.35)', borderRadius: 10,
  },
  table: { width: '100%', minWidth: 980, borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '8px 9px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    color: muted, borderBottom: border,
  },
  num: { textAlign: 'right' },
  td: { padding: '7px 9px', borderBottom: '1px solid var(--border-color, rgba(15,23,42,0.06))', verticalAlign: 'middle' },
  cellInput: { ...s.input, height: 32, padding: '4px 6px', textAlign: 'right', borderRadius: 6, width: '100%', minWidth: 60 },
  cellSelect: { ...s.select, height: 32, padding: '0 6px', borderRadius: 6, minWidth: 80 },
  empty: { padding: 14, fontSize: 13, color: muted },
  totalValue: { fontWeight: 800 },
};

const dangerBtn = {
  ...s.btnSecondary, color: 'var(--danger, #e2445c)', borderColor: 'var(--danger, #e2445c)',
  minWidth: 34, padding: '4px 9px', height: 32,
};

let seq = 0;
const newRowId = () => `row-${Date.now()}-${(seq += 1)}`;

const EMPTY_ROW = () => ({
  clientId: newRowId(),
  productId: '', productLabel: '',
  units: [], unit: '', unitMultiplier: 1,
  // #760 Kural 1: KDV oranı yeni satırda BOŞ gelir (eskiden sabit '20').
  orderedQuantity: '', unitPrice: '', vatRate: '',
});

/** Kalem tutarları — backend ile AYNI formül (net = miktar × fiyat; brüt = net × (1 + KDV/100)). */
function rowTotals(row) {
  const net = toNum(row.orderedQuantity) * toNum(row.unitPrice);
  const vat = net * (toNum(row.vatRate) / 100);
  return { net, vat, gross: net + vat };
}

/** OData sunucu-araması — 4000+ müşteri / 8000+ ürün belleğe İNDİRİLMEZ. */
function odataSearch(api, fields, extraQs = '') {
  return async (query, skip = 0) => {
    const term = String(query || '').replace(/'/g, "''").toLowerCase();
    const filter = term ? fields.map((f) => `contains(tolower(${f}),'${term}')`).join(' or ') : '';
    let url = `${api}?$top=${LOOKUP_PAGE}&$skip=${skip}&$orderby=TextId${extraQs}`;
    if (filter) url += `&$filter=${encodeURIComponent(filter)}`;
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) return [];
    return getODataRows(await res.json()).map((r) => ({ value: r.id || r.Id, label: lookupLabel(r), item: r }));
  };
}

export function NamaDocumentCreatePage() {
  const navigate = useNavigate();
  const { canAdd } = usePageActions();
  usePageMeta('Yeni Nama Sevk Belgesi', 'Sütaş adına teslim edilecek belgeyi masabaşından aç. Satıcıyı sunucu belirler.');

  const [form, setForm] = useState({
    source: NAMA_SOURCE.SAP,
    docType: 'SIPARIS',
    externalNo: '',
    customerId: '', customerLabel: '',
    representativeId: '', representativeLabel: '',
    receivedAt: new Date().toISOString().slice(0, 10),
    note: '',
  });
  const [rows, setRows] = useState([EMPTY_ROW()]);
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Menü aksiyon hakkı (9551/Add). DetailShell /new rotasında form alanlarını zaten kilitler;
  // kalem tablosu Field* dışında olduğu için burada AÇIKÇA kilitlenir.
  const locked = !canAdd;

  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));
  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchCustomers = useMemo(() => odataSearch('/api/customers', ['TextId', 'Title']), []);
  const fetchRepresentatives = useMemo(() => odataSearch('/api/representatives', ['TextId', 'Name']), []);
  const fetchProducts = useMemo(() => odataSearch('/api/products', ['TextId', 'Name'], '&light=1'), []);

  const patchRow = (clientId, patch) => setRows((cur) => cur.map((r) => (r.clientId === clientId ? { ...r, ...patch } : r)));
  const addRow = () => setRows((cur) => [...cur, EMPTY_ROW()]);
  const removeRow = (clientId) => setRows((cur) => (cur.length === 1 ? [EMPTY_ROW()] : cur.filter((r) => r.clientId !== clientId)));

  /* Ürün seçilince: birimleri ürün tanımından (ProductUnit) çek + varsa geçerli fiyatı/KDV'yi doldur.
     Fiyat bulunamazsa alan boş kalır — kullanıcı elle girer (sessiz geçilmez, uyarılır). */
  const pickProduct = useCallback(async (clientId, value, opt) => {
    const item = opt?.item;
    patchRow(clientId, {
      productId: value || '',
      productLabel: opt?.label || '',
      units: [], unit: '', unitMultiplier: 1,
    });
    if (!value) return;

    const headers = authHeaders();
    const units = await fetchProductUnits(value, headers, item?.unit1 ?? item?.Unit1);
    // #755 VARSAYILAN BİRİM: ürün kartındaki "Varsayılan Ürün Birimi" (Product.Unit = 1..5) ön-seçilir;
    // 1/boş ya da listede yoksa taban birim.
    const duo = Number(item?.unit ?? item?.Unit);
    const defaultUnitName = duo >= 1 && duo <= 5
      ? String(item?.[`unit${duo}`] ?? item?.[`Unit${duo}`] ?? '').trim()
      : '';
    const chosen = (defaultUnitName ? units.find((u) => u.name === defaultUnitName) : null) ?? units[0];
    patchRow(clientId, {
      units,
      unit: chosen?.name || '',
      unitMultiplier: chosen?.multiplier || 1,
    });

    try {
      const params = new URLSearchParams({ productId: value });
      if (form.customerId) params.append('customerId', form.customerId);
      // #760 Kural 2/3/4: KDV fiyat tanımından; fiyatta tanımlı değilse ÜRÜN KARTINDAN. Nama belgesi
      // Sütaş adına SATIŞtır → ürünün satış oranı. Eskiden fiyat yoksa satır hiç dokunulmuyor ve KDV
      // sabit '20'de kalıyordu.
      const productVat = productVatOf(item, false);
      // #760 (2. tur) Fiyat çözülemediğinde (uç hatası / ağ hatası / tanım yok) KDV yine ürün kartından
      // yazılır. Bu sayfa KDV'yi `vatRate` alanında tutuyor — ortak applyVatFallback `vatPercentage`
      // yazdığı için burada elle uygulanır.
      const vatFallback = () => {
        const vatOnly = resolveLineVat(null, productVat);
        if (vatOnly != null) patchRow(clientId, { vatRate: vatOnly });
      };
      const res = await fetch(`/api/prices/Effective?${params}`, { headers });
      if (!res.ok) { vatFallback(); return; }
      const price = await res.json();
      if (!price) { vatFallback(); return; }
      const patch = { unitPrice: price.salePrice == null ? '' : String(price.salePrice) };
      const vat = resolveLineVat(price, productVat);
      if (vat != null) patch.vatRate = vat;
      patchRow(clientId, patch);
    } catch { /* sessiz: fiyat elle girilebilir (KDV fallback'i yukarıdaki dallarda uygulandı) */ }
  }, [form.customerId]);

  // Birim değişince fiyat yeni çarpana göre ölçeklenir (#253 deseni) ve çarpan kayda geçer.
  const changeRowUnit = (row, unitName) => {
    const price = convertUnitPrice(row.unitPrice, row.units, row.unit || row.units[0]?.name, unitName);
    const mult = row.units.find((u) => u.name === unitName)?.multiplier || 1;
    patchRow(row.clientId, { unit: unitName, unitMultiplier: mult, unitPrice: price });
  };

  const totals = rows.reduce((acc, r) => {
    const t = rowTotals(r);
    return { net: acc.net + t.net, vat: acc.vat + t.vat, gross: acc.gross + t.gross };
  }, { net: 0, vat: 0, gross: 0 });

  const filledRows = rows.filter((r) => r.productId);

  const validate = () => {
    if (!form.externalNo.trim()) return 'Belge no zorunludur.';
    if (!form.customerId) return 'Müşteri seçilmelidir.';
    if (!form.representativeId) return 'Temsilci seçilmelidir.';
    if (!filledRows.length) return 'En az bir kalem girilmelidir.';
    if (filledRows.some((r) => toDecimal(r.orderedQuantity) <= 0)) return 'Kalem miktarı 0’dan büyük olmalıdır.';
    return null;
  };

  const save = async () => {
    if (saving) return;
    setTried(true);
    const err = validate();
    if (err) { showToast('error', err); return; }

    const payload = {
      source: form.source,
      docType: form.docType,
      externalNo: form.externalNo.trim(),
      customerId: form.customerId,
      representativeId: form.representativeId,
      receivedAt: form.receivedAt ? new Date(form.receivedAt).toISOString() : null,
      note: form.note || null,
      lines: filledRows.map((r) => ({
        productId: r.productId,
        unit: r.unit || 'AD',
        unitMultiplier: toDecimal(r.unitMultiplier) || 1,
        orderedQuantity: toDecimal(r.orderedQuantity),
        unitPrice: toDecimal(r.unitPrice),
        vatRate: toDecimal(r.vatRate),
      })),
    };

    setSaving(true);
    try {
      const res = await fetch(NAMA_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => null);
      // Sunucunun mesajı KULLANICIYA gösterilir: "müşteri bulunamadı", "belge zaten kapanmış",
      // "kaynak MOBILE olamaz" — hepsi burada okunabilir tek cümledir.
      if (!res.ok || body?.success === false || body?.Success === false) {
        throw new Error(body?.message || body?.Message || 'Belge kaydedilemedi');
      }
      showToast('success', 'Nama sevk belgesi oluşturuldu — Teslim Bekliyor.');
      setTimeout(() => navigate('/nama-documents'), 500); // #162 kayıt sonrası listeye dön
    } catch (e) {
      showToast('error', e.message || 'Belge kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const crumb = [
    { label: 'Nama Sevk Belgeleri', to: '/nama-documents' },
    { label: 'Yeni Belge', current: true },
  ];

  const actions = (
    <>
      <button type="button" className="dps-btn" onClick={() => navigate('/nama-documents')} disabled={saving}>Vazgeç</button>
      <button type="button" className="dps-btn primary" onClick={save} disabled={saving || !canAdd}>
        {saving ? 'Kaydediliyor…' : 'Kaydet'}
      </button>
    </>
  );

  return (
    <>
      <DetailShell crumb={crumb} actions={actions}>
        {/* SATICI BANDI — satıcı formda YOK; sunucu belirler. */}
        <div style={st.sellerBand}>
          <span style={{ fontSize: 20, lineHeight: 1 }}>🏷️</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary, #0f172a)' }}>
              Satıcı: {DEFAULT_SELLER_NAME} — sunucu belirler
            </div>
            <div style={{ fontSize: 12, color: muted, marginTop: 3 }}>
              {SELLER_STATEMENT} {SELLER_SERVER_DETERMINED}
            </div>
          </div>
        </div>

        <Section
          icon="truck"
          title="Belge"
          subtitle="Belge SAP ya da telsel'de doğar; burada masabaşından kaydedilir."
          note="Belge no (kaynak + no) BENZERSİZDİR: aynı çiftle tekrar kaydederseniz mevcut belge güncellenir. Yeni belge her zaman “Teslim Bekliyor” doğar — durum seçilmez, teslimattan türetilir."
        >
          <Grid cols={3}>
            <FieldSelect
              form={form} set={set} tried={tried} k="source" label="Kaynak" req
              options={SOURCE_CREATE_OPTIONS}
              hint="Mobil seçilemez — o, temsilcinin cihazında doğan belgenin işaretidir."
            />
            <FieldSelect
              form={form} set={set} tried={tried} k="docType" label="Belge Tipi" req
              options={DOC_TYPE_SELECT_OPTIONS}
            />
            <FieldText
              form={form} set={set} tried={tried} k="externalNo" label="Belge No" req mono
              placeholder="IRS-SAP-004120"
            />
            <FieldCustom label="Müşteri" req invalid={tried && !form.customerId}>
              <SearchableSelect
                value={form.customerId}
                selectedLabel={form.customerLabel}
                onChange={(v, opt) => setForm((c) => ({ ...c, customerId: v || '', customerLabel: opt?.label || '' }))}
                fetchOptions={fetchCustomers}
                placeholder="Müşteri ara…"
                pageSize={LOOKUP_PAGE}
              />
            </FieldCustom>
            <FieldCustom label="Temsilci" req invalid={tried && !form.representativeId}>
              <SearchableSelect
                value={form.representativeId}
                selectedLabel={form.representativeLabel}
                onChange={(v, opt) => setForm((c) => ({ ...c, representativeId: v || '', representativeLabel: opt?.label || '' }))}
                fetchOptions={fetchRepresentatives}
                placeholder="Temsilci ara…"
                pageSize={LOOKUP_PAGE}
              />
            </FieldCustom>
            <FieldDate form={form} set={set} k="receivedAt" label="Geliş Tarihi" />
            <FieldTextarea form={form} set={set} k="note" label="Not" rows={2} span />
          </Grid>
        </Section>

        <Section
          icon="list"
          title="Kalemler"
          subtitle="En az bir kalem zorunludur; miktar 0’dan büyük olmalıdır."
          right={(
            <button type="button" className="dps-btn" onClick={addRow} disabled={locked}>+ Kalem Ekle</button>
          )}
        >
          <div style={{ overflowX: 'auto' }}>
            <table style={st.table}>
              <thead>
                <tr>
                  <th style={{ ...st.th, width: 34, textAlign: 'center' }}>#</th>
                  <th style={st.th}>Ürün</th>
                  <th style={{ ...st.th, ...st.num, width: 110 }}>Miktar</th>
                  <th style={{ ...st.th, width: 110 }}>Birim</th>
                  <th style={{ ...st.th, ...st.num, width: 120 }}>Birim Fiyat</th>
                  <th style={{ ...st.th, ...st.num, width: 90 }}>KDV %</th>
                  <th style={{ ...st.th, ...st.num, width: 130 }}>Tutar</th>
                  <th style={{ ...st.th, width: 44 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const t = rowTotals(row);
                  const unitNames = row.units.map((u) => u.name);
                  return (
                    <tr key={row.clientId}>
                      <td style={{ ...st.td, textAlign: 'center', color: muted }}>{idx + 1}</td>
                      <td style={{ ...st.td, minWidth: 280 }}>
                        <SearchableSelect
                          value={row.productId}
                          selectedLabel={row.productLabel}
                          onChange={(v, opt) => pickProduct(row.clientId, v, opt)}
                          fetchOptions={fetchProducts}
                          placeholder="Ürün kodu / adı ara…"
                          pageSize={LOOKUP_PAGE}
                          disabled={locked}
                        />
                      </td>
                      <td style={{ ...st.td, ...st.num }}>
                        <input
                          inputMode="decimal" style={st.cellInput} value={row.orderedQuantity} placeholder="0"
                          disabled={locked}
                          onChange={(e) => patchRow(row.clientId, { orderedQuantity: sanitizeDecimal(e.target.value) })}
                        />
                      </td>
                      <td style={st.td}>
                        <select
                          style={st.cellSelect}
                          value={row.unit || ''}
                          disabled={locked}
                          onChange={(e) => changeRowUnit(row, e.target.value)}
                        >
                          {unitNames.length === 0 && <option value="">AD</option>}
                          {unitNames.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </td>
                      <td style={{ ...st.td, ...st.num }}>
                        <input
                          inputMode="decimal" style={st.cellInput} value={row.unitPrice} placeholder="0"
                          disabled={locked}
                          onChange={(e) => patchRow(row.clientId, { unitPrice: sanitizeDecimal(e.target.value) })}
                        />
                      </td>
                      <td style={{ ...st.td, ...st.num }}>
                        <input
                          inputMode="decimal" style={st.cellInput} value={row.vatRate} placeholder="0"
                          disabled={locked}
                          onChange={(e) => patchRow(row.clientId, { vatRate: sanitizeDecimal(e.target.value) })}
                        />
                      </td>
                      <td style={{ ...st.td, ...st.num, fontWeight: 700 }}>{fmtMoney(t.net)}</td>
                      <td style={{ ...st.td, textAlign: 'center' }}>
                        <button type="button" style={dangerBtn} title="Satırı sil" disabled={locked}
                                onClick={() => removeRow(row.clientId)}>×</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ ...st.td, fontWeight: 800 }} colSpan={6}>Ara Toplam (KDV hariç)</td>
                  <td style={{ ...st.td, ...st.num, ...st.totalValue }}>{fmtMoney(totals.net)}</td>
                  <td style={st.td} />
                </tr>
                <tr>
                  <td style={{ ...st.td, color: muted }} colSpan={6}>KDV</td>
                  <td style={{ ...st.td, ...st.num, color: muted }}>{fmtMoney(totals.vat)}</td>
                  <td style={st.td} />
                </tr>
                <tr>
                  <td style={{ ...st.td, fontWeight: 800 }} colSpan={6}>Genel Toplam (KDV dahil)</td>
                  <td style={{ ...st.td, ...st.num, ...st.totalValue }}>{fmtMoney(totals.gross)}</td>
                  <td style={st.td} />
                </tr>
              </tfoot>
            </table>
          </div>
          {!filledRows.length && (
            <div style={st.empty}>Henüz ürün seçilmedi — en az bir kalem girmeden belge kaydedilemez.</div>
          )}
        </Section>
      </DetailShell>

      <Toast toast={toast} />
    </>
  );
}

export default NamaDocumentCreatePage;
